/**
 * The whole public site while the gate is on: wordmark, one line, an email box.
 * No providers, no router, no auth — loads instantly and stays up under a flood.
 */

import React, { useEffect, useRef, useState } from 'react';

type Status = 'idle' | 'sending' | 'done' | 'error';

const PENDING_KEY = 'wallos_waitlist_pending';

function detectSource(): string {
  try {
    const p = new URLSearchParams(window.location.search);
    const utm = p.get('utm_source') || p.get('source') || p.get('ref');
    if (utm) return utm.slice(0, 64);
    const ref = document.referrer.toLowerCase();
    if (ref.includes('tiktok')) return 'tiktok';
    if (ref.includes('instagram')) return 'instagram';
    if (ref.includes('twitter') || ref.includes('x.com')) return 'x';
    if (ref.includes('youtube')) return 'youtube';
    if (ref.includes('reddit')) return 'reddit';
    return ref ? 'referral' : 'direct';
  } catch {
    return 'direct';
  }
}

async function join(email: string): Promise<{ ok: boolean; already?: boolean; count?: number | null; error?: string }> {
  const res = await fetch('/api/waitlist', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, source: detectSource(), referrer: document.referrer || null }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data?.error || `Something went wrong (${res.status}).` };
  return { ok: true, already: !!data.already, count: typeof data.count === 'number' ? data.count : null };
}

export const WaitlistPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [count, setCount] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = 'Wall OS — Join the waitlist';
    fetch('/api/waitlist/count')
      .then((r) => r.json())
      .then((d) => typeof d?.count === 'number' && setCount(d.count))
      .catch(() => {});

    // Retry a signup that failed on a previous visit (API hiccup).
    const pending = localStorage.getItem(PENDING_KEY);
    if (pending) {
      join(pending)
        .then((r) => r.ok && localStorage.removeItem(PENDING_KEY))
        .catch(() => {});
    }
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      setStatus('error');
      setMessage('That email does not look right.');
      inputRef.current?.focus();
      return;
    }
    setStatus('sending');
    try {
      const r = await join(value);
      if (r.ok) {
        localStorage.removeItem(PENDING_KEY);
        setStatus('done');
        if (typeof r.count === 'number') setCount(r.count);
        setMessage(r.already ? 'You are already on the list. We will be in touch.' : 'You are on the list.');
      } else {
        localStorage.setItem(PENDING_KEY, value);
        setStatus('error');
        setMessage(r.error || 'Could not save that. Try again in a moment.');
      }
    } catch {
      localStorage.setItem(PENDING_KEY, value);
      setStatus('error');
      setMessage('Network hiccup. Your email is saved on this device; it will retry when you come back, or try again now.');
    }
  };

  return (
    <main className="min-h-screen w-full bg-black text-white flex flex-col items-center justify-center px-6 select-none">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            'radial-gradient(60% 50% at 50% 40%, rgba(6,182,212,0.18) 0%, rgba(6,182,212,0.05) 40%, rgba(0,0,0,0) 70%)',
        }}
      />

      <div className="relative w-full max-w-md text-center">
        <p className="text-[11px] font-bold tracking-[0.5em] uppercase text-amber-300 mb-6">Wall OS · Ceiling Reading</p>
        <h1 className="text-4xl sm:text-5xl font-serif leading-tight">Lie back.<br />Your ceiling reads to you.</h1>
        <p className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed">
          Point any projector at the ceiling. A voice reads 100+ classic books
          <br className="hidden sm:block" /> while every word lights up overhead. Your phone is the remote.
        </p>

        {status === 'done' ? (
          <div className="mt-10 rounded-2xl border border-cyan-500/30 bg-cyan-500/10 px-6 py-5">
            <p className="text-cyan-200 font-semibold">{message}</p>
            <p className="mt-1 text-sm text-slate-400">We will email you the moment your ceiling can read.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3" noValidate>
            <input
              ref={inputRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="you@email.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === 'error') setStatus('idle');
              }}
              disabled={status === 'sending'}
              className="flex-1 rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-base text-white placeholder:text-slate-500 outline-none focus:border-cyan-400 focus:bg-white/10 transition disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={status === 'sending'}
              className="rounded-xl bg-cyan-400 px-6 py-3.5 text-base font-bold text-black hover:bg-cyan-300 active:scale-[0.98] transition disabled:opacity-60"
            >
              {status === 'sending' ? 'Joining…' : 'Join the waitlist'}
            </button>
          </form>
        )}

        <p className={`mt-4 min-h-[1.5rem] text-sm ${status === 'error' ? 'text-amber-400' : 'text-slate-500'}`}>
          {status === 'error'
            ? message
            : count !== null && count >= 25
              ? `${count.toLocaleString()} people waiting`
              : 'Early access. No spam.'}
        </p>
      </div>

      <footer className="fixed bottom-5 inset-x-0 text-center text-[11px] tracking-[0.2em] uppercase text-slate-600">
        © {new Date().getFullYear()} Wall OS
      </footer>
    </main>
  );
};

export default WaitlistPage;
